import { apiWrite } from "@/api/client";
import type { ConfirmRefusal } from "@/components/ConfirmDialog";
import {
  doneState,
  errorState,
  type FormState,
  parseFormData,
} from "@/lib/formState";
import {
  type InlineCreateResult,
  inlineFieldError,
  parseInline,
} from "@shared/inlineCreate";
import { formatTemplateRefs } from "@shared/usage";
import {
  traitCreateBody,
  traitSchema,
  traitUpdateSchema,
} from "@shared/validation";
import { refusalFrom } from "./refusal";

export async function createTraitAction(
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(traitSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  const result = await apiWrite<{ id: number }>("/traits", {
    method: "POST",
    body: parsed.value,
  });

  if (!result.isOk) {
    return errorState(parsed.values, {
      // An archived trait still holds its code, and duplicates here are worse
      // than a messy list: two spellings mean a gate that matches for some
      // users and not others, with nothing in the data explaining why.
      code: ["A trait with this code already exists (it may be archived)."],
    });
  }

  return doneState("/traits");
}

export type TraitInlineChoice = {
  id: number;
  code: string;
  label: string;
};

// Minted from the quest template form. The survey wording and its sort order
// are left at their defaults rather than asked for here: a trait is bound
// during authoring, and whether it is also an onboarding question is a
// decision about the signup flow, made on /traits with the whole survey in
// view.
export async function createTraitInlineAction(input: {
  code: string;
  label: string;
}): Promise<InlineCreateResult<TraitInlineChoice>> {
  const parsed = parseInline(traitCreateBody, input);

  if (!parsed.wasParsed) {
    return { wasCreated: false, fieldErrors: parsed.fieldErrors };
  }

  const result = await apiWrite<{ id: number }>("/traits", {
    method: "POST",
    body: parsed.value,
  });

  if (!result.isOk) {
    return inlineFieldError(
      "code",
      "A trait with this code already exists (it may be archived).",
    );
  }

  return {
    wasCreated: true,
    choice: {
      id: result.value.id,
      code: parsed.value.code,
      label: parsed.value.label,
    },
  };
}

export async function updateTraitAction(
  id: number,
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(traitUpdateSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  await apiWrite(`/traits/${id}`, { method: "PUT", body: parsed.value });

  return doneState("/traits");
}

export async function archiveTraitAction(id: number): Promise<ConfirmRefusal> {
  await apiWrite(`/traits/${id}/deactivate`, { method: "POST" });

  return null;
}

export async function restoreTraitAction(id: number): Promise<void> {
  await apiWrite(`/traits/${id}/activate`, { method: "POST" });
}

// The dialog only opens on archived rows, but the checks run again
// server-side: a binding written since the listing rendered refuses in place.
export async function deleteTraitAction(id: number): Promise<ConfirmRefusal> {
  const result = await apiWrite(`/traits/${id}`, { method: "DELETE" });

  return refusalFrom(result, {
    inUse: (templates) =>
      `Quest templates still require or grant this trait: ${formatTemplateRefs(templates)}. Delete or unbind them first.`,
    missing: "This trait no longer exists.",
    stillActive:
      "This trait has been restored since the page loaded. Archive it again first.",
  });
}
